import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface YellowPrecipitateProps {
  position?: [number, number, number];
  /** 0 = no precipitate, 1 = fully formed. */
  formationProgress?: number;
  /** Cylindrical bounds of the test tube interior (the particles stay within). */
  containerRadius?: number;
  /** Vertical extent of the precipitate column. */
  columnHeight?: number;
  /** Maximum particle instances. */
  particleCount?: number;
  /** If true, particles swirl (used during shake animation). */
  swirling?: boolean;
}

/**
 * Yellow PbI₂ precipitate — small instanced yellow particles that
 * progressively fill a test-tube interior. Used in Part C of
 * Activity 3.18 when Pb(NO₃)₂ and KI solutions are mixed.
 *
 * Adapted from the SaltParticles instanced-mesh pattern. Particles are
 * scattered within a cylindrical volume; `formationProgress` controls
 * how many are visible. While `swirling=true`, particles slowly rotate
 * around the central Y axis to communicate the shake animation.
 */
export function YellowPrecipitate({
  position = [0, 0, 0],
  formationProgress = 0,
  containerRadius = 0.045,
  columnHeight = 0.14,
  particleCount = 140,
  swirling = false,
}: YellowPrecipitateProps) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const seeds = useMemo(() => {
    const out: { r: number; theta: number; y: number; size: number; spin: number }[] = [];
    let s = 41;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    for (let i = 0; i < particleCount; i++) {
      out.push({
        r: Math.sqrt(rand()) * (containerRadius * 0.85),
        theta: rand() * Math.PI * 2,
        y: rand() * columnHeight,
        size: 0.0028 + rand() * 0.0028,
        spin: rand() * Math.PI * 2,
      });
    }
    return out;
  }, [particleCount, containerRadius, columnHeight]);

  useFrame((s) => {
    if (!ref.current) return;
    const t = s.clock.elapsedTime;
    const visible = Math.floor(particleCount * Math.max(0, Math.min(1, formationProgress)));
    for (let i = 0; i < particleCount; i++) {
      if (i >= visible) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        ref.current.setMatrixAt(i, dummy.matrix);
        continue;
      }
      const p = seeds[i];
      const theta = swirling ? p.theta + t * 1.4 : p.theta;
      const x = position[0] + Math.cos(theta) * p.r;
      const z = position[2] + Math.sin(theta) * p.r;
      const y = position[1] + p.y;
      dummy.position.set(x, y, z);
      dummy.scale.setScalar(p.size);
      dummy.rotation.set(p.spin, t * 0.6 + p.spin, 0);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, particleCount]}>
      <icosahedronGeometry args={[1, 0]} />
      <meshStandardMaterial color="#f5c842" roughness={0.85} metalness={0.05} flatShading />
    </instancedMesh>
  );
}

export default YellowPrecipitate;
